/**
 * `contacts.suppressions` — the per-contact refusals (design §1.4, §4.7).
 *
 * Three flags on one row, because they are one conversation with a person rather
 * than three features:
 *
 *  - `noRecording` — the do-not-record flag the recorder honours automatically.
 *    §4.3: the app "refuses to arm and says why". A manual "Record now" is
 *    refused too, because a flag a button overrides is advisory, and this one is
 *    not.
 *  - `noEnrichment` — GDPR erasure, honoured forever (§3.2's gate). Checked on
 *    the write path as well as the read, because a queued job can predate it.
 *  - `noOutreach` — Hermes never drafts to them.
 *
 * ## Why `remove` is a POST rather than a DELETE
 *
 * It is not a delete. `key_hash` is what makes an erasure survive the contact's
 * own deletion, so lifting a flag CLEARS it and keeps the row when there is
 * still something to remember. Modelling that as `DELETE /suppressions/{id}`
 * would promise a row disappearance the server must not perform — and the
 * contract already forbids a body on DELETE, which the per-flag payload needs.
 */

import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.ts';

export const SUPPRESSION_FLAGS = ['noRecording', 'noEnrichment', 'noOutreach'] as const;
export type SuppressionFlagName = (typeof SUPPRESSION_FLAGS)[number];
export const suppressionFlagSchema = z.enum(SUPPRESSION_FLAGS);

export const contactSuppressionSchema = z.object({
  id: uuidSchema,
  contactId: uuidSchema,
  /** Denormalised so the privacy screen renders without an N+1 over contacts. */
  contactName: z.string(),
  noRecording: z.boolean(),
  noEnrichment: z.boolean(),
  noOutreach: z.boolean(),
  reason: z.string().nullable(),
  createdAt: isoDateTimeSchema,
});

export const suppressionsListInputSchema = z.object({
  /** Narrow to one flag; the privacy screen's "do not record" section. */
  flag: suppressionFlagSchema.optional(),
  limit: z.int().min(1).max(500).default(200),
});

export const suppressionsListOutputSchema = z.object({
  items: z.array(contactSuppressionSchema),
});

/**
 * PATCH semantics: an absent flag is UNTOUCHED, not cleared.
 *
 * A privacy screen shows one checkbox per flag and may show only one of them;
 * under any other reading, a screen that renders "do not record" alone would
 * silently erase a GDPR suppression the user never saw.
 */
export const suppressionsCreateInputSchema = z.object({
  contactId: uuidSchema,
  noRecording: z.boolean().optional(),
  noEnrichment: z.boolean().optional(),
  noOutreach: z.boolean().optional(),
  reason: z.string().max(500).nullish(),
});

export const suppressionsRemoveInputSchema = z.object({
  contactId: uuidSchema,
  /** Flags to CLEAR. Omitting all of them clears every flag. */
  noRecording: z.boolean().optional(),
  noEnrichment: z.boolean().optional(),
  noOutreach: z.boolean().optional(),
});

export const suppressionsRemoveOutputSchema = z.object({
  ok: z.literal(true),
  contactId: uuidSchema,
  /** True when the row was removed outright: nothing left to remember. */
  deleted: z.boolean(),
  /** What survives — a cleared row still carrying an erasure hash, or null. */
  remaining: contactSuppressionSchema.nullable(),
});

export type ContactSuppression = z.output<typeof contactSuppressionSchema>;
export type SuppressionsListOutput = z.output<typeof suppressionsListOutputSchema>;

// --- GDPR: export + account deletion (Wave-4 lane S) -------------------------

/**
 * `privacy.export` (Art. 15, portability) and `privacy.deleteAccount` (Art. 17,
 * erasure — also App Store guideline 5.1.1(v), which requires in-app account
 * deletion for anything that lets a user create an account).
 *
 * ## Why the download is a second procedure and not a URL
 *
 * `export` returns an EXPORT ID, not a link. A presigned S3 URL is a bearer
 * credential for a complete copy of a tenant's CRM with no tenant check attached
 * — the identical argument `messages.body` makes about a private email body — so
 * the bundle is streamed back through `exportDownload` under the same
 * `ActorContext` that guards every other read.
 *
 * ## Why deletion is two procedures and not one
 *
 * An irreversible erasure behind a single authenticated POST is one mis-tapped
 * button, one replayed request or one confused deputy away from destroying a
 * tenant, and there is no undo. `request` returns a short-lived token; `confirm`
 * presents it. The token proves the confirm came from the human who read the
 * warning.
 */

export const EXPORT_SECTION_NAMES = [
  'contacts',
  'contactEmails',
  'contactPhones',
  'interactions',
  'commitments',
  'followUps',
  'digests',
  'digestItems',
  'messageThreads',
  'messages',
  'events',
  'lists',
] as const;

export const exportSectionSchema = z.enum(EXPORT_SECTION_NAMES);

export const privacyExportInputSchema = z.object({
  /**
   * Cap on rows per section. The bundle says when it hit the cap rather than
   * silently shipping a prefix — a portability export that quietly omitted half
   * a timeline would be worse than none.
   */
  maxRowsPerSection: z.int().min(1).max(50_000).default(20_000),
});

export const privacyExportOutputSchema = z.object({
  exportId: uuidSchema,
  bytes: z.int().min(0),
  /** Rows written per section: what the user actually got. */
  sectionCounts: z.record(exportSectionSchema, z.int().min(0)),
  /** True when a section hit `maxRowsPerSection`. */
  truncated: z.boolean(),
  expiresAt: isoDateTimeSchema,
  /**
   * Null when the deployment has no blob store configured, in which case the
   * document is in `privacy.exportDownload` only for as long as this process
   * lives — see the handler. A legible state, not an error.
   */
  storage: z.enum(['aws-s3', 'memory', 'directory']),
});

export const privacyExportDownloadInputSchema = z.object({
  exportId: uuidSchema,
});

export const privacyExportDownloadOutputSchema = z.object({
  exportId: uuidSchema,
  contentType: z.literal('application/json'),
  /** The bundle itself, streamed through the API. Null when the blob is gone. */
  document: z.string().nullable(),
  bytes: z.int().min(0),
  truncated: z.boolean(),
  expiresAt: isoDateTimeSchema,
});

export const privacyDeleteRequestInputSchema = z.object({
  /**
   * Typed confirmation, echoed by the client. Not a security control — the token
   * is — but it is what makes the request deliberate rather than a stray tap on
   * a settings row.
   */
  acknowledgement: z.literal('DELETE MY ACCOUNT'),
});

export const privacyDeleteRequestOutputSchema = z.object({
  deletionId: uuidSchema,
  /** Plaintext, returned exactly once. Only the hash is stored. */
  token: z.string(),
  expiresAt: isoDateTimeSchema,
});

export const privacyDeleteConfirmInputSchema = z.object({
  token: z.string().min(20),
});

export const privacyDeleteConfirmOutputSchema = z.object({
  ok: z.literal(true),
  deletionId: uuidSchema,
  /** Rows removed per table. The record a support question is answered from. */
  stats: z.record(z.string(), z.int().min(0)),
  /** Erasure tombstones written, so nobody erased is ever re-enriched. */
  suppressionsWritten: z.int().min(0),
  /** True when the user row itself went (no memberships left anywhere). */
  userDeleted: z.boolean(),
  /** True when this token had already been confirmed: a replay, not an error. */
  alreadyCompleted: z.boolean(),
});

export type PrivacyExportOutput = z.output<typeof privacyExportOutputSchema>;
export type PrivacyDeleteConfirmOutput = z.output<typeof privacyDeleteConfirmOutputSchema>;
